import { AutocompleteWithLabel } from '@adotel/components'
import { LazyQueryExecFunction, OperationVariables } from '@apollo/client'
import { ButtonBase, Typography } from '@material-ui/core'
import AddDateRangeIcon from '@material-ui/icons/AddCircleOutline'
import React, { FC } from 'react'

import TimePeriodForm from '../../components/TimePeriodForm'
import { DateObject } from '../../helpers/date'
import { getTranslate, lang } from '../../localization'
import {
  Accommodation,
  AccommodationDataWithRatePlans
} from '../../types/accommodation'
import { ProviderSubmit } from '../../types/accommodationProviderSubmit'
import { Tenant } from '../../types/tenants'
import InventoryTypeSelection from './InventoryTypeSelection'
import useStyles from './styles/InventoryBulkUpdate.style'

interface InventoryBasicFiltersProps {
  hasAccessToChangeTenant: boolean
  autoCompleteForTenant: any
  setAutoCompleteForTenant: React.Dispatch<any>
  tenants: Tenant[]
  loadingForTenantsList: boolean
  setCancelPolicyValue: React.Dispatch<
    React.SetStateAction<{
      label: string
      value: string
    } | null>
  >
  setModifyPolicyValue: React.Dispatch<
    React.SetStateAction<{
      label: string
      value: string
    } | null>
  >
  hasAccessToChangeAccommodation: boolean
  loadingForAccommodation: boolean
  autoCompleteForAccommodation: AccommodationDataWithRatePlans[]
  setAutoCompleteForAccommodation: React.Dispatch<
    React.SetStateAction<AccommodationDataWithRatePlans[]>
  >
  handleChangeSelectedAccommodations: (
    newAccList: AccommodationDataWithRatePlans[]
  ) => void
  isChannelSelected: string
  setIsChannelSelected: React.Dispatch<React.SetStateAction<string>>
  selectedChannel: any[]
  setSelectedChannel: React.Dispatch<React.SetStateAction<any[]>>
  selectedDates: DateObject[]
  setselectedDates: React.Dispatch<React.SetStateAction<DateObject[]>>
  activeDatePickerIndex: number
  setActiveDatePickerIndex: React.Dispatch<React.SetStateAction<number>>
  timePickerSelected: boolean
  setTimePickerSelected: React.Dispatch<React.SetStateAction<boolean>>
  onDoneClickForCloseDatePicker: boolean
  setOnDoneClickForCloseDatePicker: React.Dispatch<
    React.SetStateAction<boolean>
  >
  loadingForProviderSubmitList: boolean
  providerData: ProviderSubmit[] | undefined
  autoCompleteForProvider: any
  setAutoCompleteForProvider: React.Dispatch<any>
  accommodationsListForSelectedTenant: Accommodation[]
  getAccommodations: LazyQueryExecFunction<
    {
      accommodations: Accommodation[]
    },
    OperationVariables
  >
  isRoleProvider: boolean | undefined
}

const InventoryBasicFilters: FC<InventoryBasicFiltersProps> = ({
  hasAccessToChangeTenant,
  autoCompleteForTenant,
  setAutoCompleteForTenant,
  tenants,
  loadingForTenantsList,
  setCancelPolicyValue,
  setModifyPolicyValue,
  hasAccessToChangeAccommodation,
  loadingForAccommodation,
  autoCompleteForAccommodation,
  setAutoCompleteForAccommodation,
  handleChangeSelectedAccommodations,
  isChannelSelected,
  setIsChannelSelected,
  selectedChannel,
  setSelectedChannel,
  selectedDates,
  setselectedDates,
  activeDatePickerIndex,
  setActiveDatePickerIndex,
  timePickerSelected,
  setTimePickerSelected,
  onDoneClickForCloseDatePicker,
  setOnDoneClickForCloseDatePicker,
  loadingForProviderSubmitList,
  providerData,
  autoCompleteForProvider,
  setAutoCompleteForProvider,
  accommodationsListForSelectedTenant,
  getAccommodations,
  isRoleProvider
}) => {
  const classes = useStyles()

  let propertyCodes: string[] = []
  if (autoCompleteForAccommodation && autoCompleteForAccommodation.length > 0) {
    autoCompleteForAccommodation.forEach((ele) => {
      propertyCodes.push(ele.value)
    })
  }

  return (
    <div className={classes.basicFiltersContainer}>
      <div className={classes.form}>
        {hasAccessToChangeTenant && (
          <AutocompleteWithLabel
            value={autoCompleteForTenant}
            label={getTranslate('Tenant')}
            placeholder={getTranslate('Select tenant')}
            includeInputInList
            onChange={(_, v: any) => {
              if (v) {
                setAutoCompleteForTenant({
                  value: v.value,
                  label: v.label,
                  id: v.id,
                  code: v.code
                })
                getAccommodations({
                  variables: {
                    tenantId: v.id
                  }
                })
              } else {
                setAutoCompleteForTenant(null)
              }

              setAutoCompleteForAccommodation([])
              if (!isRoleProvider) {
                setAutoCompleteForProvider(null)
              }
            }}
            getOptionSelected={(option, value) =>
              option.value === value.value && option.label === value.label
            }
            getOptionLabel={(option) => option.label || ''}
            options={
              tenants.map((item) => ({
                label: item.basicDetails.name,
                value: item.basicDetails?.tenantCode || '',
                id: item.basicDetails?.tenantId || '',
                code: item.basicDetails.displayTenantCode
              })) || []
            }
            renderOption={(option) => <>{option.label}</>}
            size="small"
            inputSize="small"
            loading={loadingForTenantsList}
            loadingText={getTranslate('Loading') + '...'}
          />
        )}
        {hasAccessToChangeAccommodation && (
          <AutocompleteWithLabel
            value={
              autoCompleteForAccommodation &&
              autoCompleteForAccommodation.length > 0
                ? (autoCompleteForAccommodation[0] as any)
                : autoCompleteForAccommodation
            }
            label={getTranslate('Accommodation')}
            placeholder={getTranslate('Select accommodations')}
            onChange={(_, v: any) => {
              if (v) {
                handleChangeSelectedAccommodations([v])
              } else {
                handleChangeSelectedAccommodations([])
              }
              if (!isRoleProvider) {
                setAutoCompleteForProvider(null)
              }
            }}
            getOptionSelected={(option, value) =>
              option.value === value.value && option.label === value.label
            }
            getOptionLabel={(option) => option.label || ''}
            options={
              accommodationsListForSelectedTenant
                ? accommodationsListForSelectedTenant.map((item) => ({
                    label: item.basicDetails.name,
                    value: item.basicDetails.propertyCode,
                    id: item.basicDetails.accommodationID,
                    tenantCode: item.basicDetails.tenantCode,
                    tenantID: item.basicDetails.tenantId
                  }))
                : [{ label: getTranslate('Nothing found'), value: '' }]
            }
            size="small"
            inputSize="small"
            loading={loadingForAccommodation}
            loadingText={getTranslate('Loading') + '...'}
          />
        )}
        <AutocompleteWithLabel
          value={autoCompleteForProvider}
          size="small"
          inputSize="small"
          label={getTranslate('Provider')}
          placeholder={getTranslate('Select provider')}
          onChange={(_, v: any) => {
            if (v) {
              setAutoCompleteForProvider({
                value: v.value,
                label: v.label
              })
            } else {
              setAutoCompleteForProvider(null)
            }
            setCancelPolicyValue(null)
            setModifyPolicyValue(null)
          }}
          getOptionLabel={(option) => option.label || ''}
          getOptionSelected={(option, value) => option.value === value.value}
          options={
            providerData
              ? providerData.map((p) => ({
                  label:
                    lang === 'fa' &&
                    p.providerNameTranslatedList &&
                    p.providerNameTranslatedList[0].value
                      ? p.providerNameTranslatedList[0].value
                      : p.providerName || '',
                  value: p.providerCode || ''
                }))
              : []
          }
          disabled={isRoleProvider}
          loading={loadingForProviderSubmitList}
          loadingText={getTranslate('Loading') + '...'}
        />
        <InventoryTypeSelection
          isChannelSelected={isChannelSelected}
          selectedChannel={selectedChannel}
          setIsChannelSelected={setIsChannelSelected}
          setSelectedChannel={setSelectedChannel}
          propertyCodes={propertyCodes}
        />
      </div>
      <div className={classes.dateSelectorForm}>
        <Typography>{getTranslate('Time Periods')}</Typography>
        {selectedDates.map((dateRangeItem, index) => (
          <TimePeriodForm
            // just to skip warning. there's no good choise for key in here
            key={index.toString()}
            isOpen={activeDatePickerIndex === index && timePickerSelected}
            toggleDatePicker={() => {
              setActiveDatePickerIndex(
                activeDatePickerIndex === index ? -1 : index
              )
              setOnDoneClickForCloseDatePicker(true)
            }}
            dateObject={dateRangeItem}
            onChange={(value) =>
              setselectedDates((currentList) => [
                ...currentList.slice(0, index),
                value,
                ...currentList.slice(index + 1)
              ])
            }
            deletable={index !== 0}
            onDelete={() => {
              if (index !== 0)
                setselectedDates((currentList) => [
                  ...currentList.slice(0, index),
                  ...currentList.slice(index + 1)
                ])
            }}
            onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
            setOnDoneClickForCloseDatePicker={setOnDoneClickForCloseDatePicker}
            setTimePickerSelected={setTimePickerSelected}
          />
        ))}
        <ButtonBase
          className={classes.addMoreDateRangesButton}
          onClick={() =>
            setselectedDates((currentList) => [...currentList, {}])
          }>
          <AddDateRangeIcon className={classes.addMoreDateRangeIcon} />
          <Typography>{getTranslate('More Time Periods')}</Typography>
        </ButtonBase>
      </div>
    </div>
  )
}

export default InventoryBasicFilters
